// Shared completion queue for the async builtins (db_get / db_set /
// db_del / db_list / fetch).
//
// The spec ("fire callbacks on the script's execution thread between
// event handlers, never re-enter an in-flight handler" — see
// docs/persistence.md §Callbacks and docs/fetch.md) splits the work in
// two: the env import only ENQUEUES a completion, and the host decides
// WHEN to drain — typically right after every event_* invocation. This
// class owns the queue, the dispatcher resolution (via the codegen's
// CALLBACK_DISPATCHERS contract, so the "__cb_db_get" strings live in
// exactly one place), and the db_error / fetch_error diagnostic-event
// firing. Hosts own only the push sites and the drain points.
//
// Every dispatch is wrapped per-op: a trapping callback is logged and
// the drain continues with the remaining ops, so one bad callback can't
// wedge every completion queued behind it.
import { CALLBACK_DISPATCHERS } from "../codegen/import-contract.js";
import { allocString, allocStringArray, encodeBytes, } from "./memory-utils.js";
// Fixpoint bound on drain(): a callback that re-issues an async op on every
// completion (`db_set(..., fn() { db_set(...) })`) makes drain() loop forever.
// Fuel can't bound it — each dispatcher entry resets fuel — and the db_write
// path allocates nothing per iteration, so the heap cap doesn't trip either.
// Past this many dispatch rounds we sever the chain (drop the queue + abandon
// outstanding ops) and warn, and flag it so the deterministic driver (settle /
// the reference host) fails hard. This is the async twin of the server's
// MAX_SETTLE_ROUNDS `tell` bound. A legitimate sequential async chain is far
// under this; only a non-terminating cycle reaches it.
const MAX_DRAIN_ROUNDS = 10_000;
export class AsyncOpQueue {
    h;
    getExports;
    pending = [];
    // Bumped on every clear() (wasm instance replacement). A push() carries
    // the epoch captured when its op was issued; a completion that resolves
    // after the instance was swapped therefore carries a stale epoch and is
    // dropped, so it can't dispatch a now-meaningless cbId against the new
    // instance. Capture via epochNow() at issue time (synchronously, in the
    // env import), not at push time (which runs in a later microtask, after
    // any intervening clear()).
    epoch = 0;
    // Outstanding backend operations whose completion hasn't been pushed
    // yet. Each db_*/fetch import registers the promise that ends in a
    // push(); drain() awaits these to quiescence before/while dispatching,
    // so a callback never fires before the op it's waiting on has landed.
    inFlight = new Set();
    // Coalesces overlapping drains. Hosts kick off a fire-and-forget drain
    // after every event (so a slow backend never stalls the dispatch loop);
    // a second drain that arrives while one is running joins it rather than
    // racing the shared pending queue.
    draining = null;
    // Set true when the most recent drain() severed a non-terminating callback
    // cycle at MAX_DRAIN_ROUNDS. Reset at the start of each fresh drain. The
    // deterministic drivers read it (via hitDrainLimit) and throw; the prod
    // fire-and-forget path just drops + warns and ignores it.
    drainLimitHit = false;
    label;
    role;
    log;
    onDbError;
    onFetchError;
    // `getExports` is a thunk, not a snapshot: hops and hot reloads swap
    // the wasm instance under the host, and the dispatchers (plus the
    // cbId table they switch on) swap with it. Hosts must clear() the
    // queue when they re-instantiate.
    constructor(h, getExports, opts) {
        this.h = h;
        this.getExports = getExports;
        this.label = opts.label;
        this.role = opts.role ?? "both";
        this.log = opts.log ?? ((msg, err) => console.error(msg, err));
        this.onDbError = opts.onDbError ?? null;
        this.onFetchError = opts.onFetchError ?? null;
    }
    // Snapshot the current instance epoch. Hosts capture this synchronously
    // when an async op is issued and hand it back to the matching push*()
    // call, so a completion that lands after a clear() is recognized as stale.
    epochNow() {
        return this.epoch;
    }
    pushDbGet(epoch, op) {
        if (epoch !== this.epoch)
            return;
        this.pending.push({ kind: "db_get", ...op });
    }
    pushDbWrite(epoch, op) {
        if (epoch !== this.epoch)
            return;
        this.pending.push({ kind: "db_write", ...op });
    }
    pushDbList(epoch, op) {
        if (epoch !== this.epoch)
            return;
        this.pending.push({ kind: "db_list", ...op });
    }
    pushFetch(epoch, op) {
        if (epoch !== this.epoch)
            return;
        this.pending.push({ kind: "fetch", ...op });
    }
    // Register an outstanding backend operation. `p` must be the promise
    // chain that ends in the matching push*() call, so that once it
    // settles the completion is queued. db_*/fetch imports call this; the
    // import itself returns void immediately (fire-and-forget), and drain()
    // awaits the registered promises before delivering callbacks.
    track(p) {
        this.inFlight.add(p);
        void p.finally(() => this.inFlight.delete(p));
    }
    // Drop everything queued — call when the wasm instance is replaced.
    // In-flight promises are abandoned too: a stale completion that resolves
    // after a hot reload must not dispatch against the new instance.
    clear() {
        this.epoch++;
        this.pending.length = 0;
        this.inFlight.clear();
    }
    // Run the async-op queue to quiescence: wait for outstanding backend
    // ops to land their completions, dispatch what's queued, and repeat
    // until nothing is in flight and nothing is pending. Dispatch is FIFO,
    // one op at a time, so callbacks that themselves issue fresh db_*/fetch
    // ops get serialized in issue order. Determinism holds: we only await
    // already-issued ops (no timers, no wall-clock) and deliver in order.
    // The caller must invoke this only at safe points (between event
    // handlers — never from inside one).
    drain() {
        // Coalesce: a drain that arrives while one is running joins it. New
        // ops that arrive mid-drain are caught by the loop's re-check (they
        // come either from a dispatched callback or from a later event that
        // kicks off its own drain).
        if (this.draining)
            return this.draining;
        this.drainLimitHit = false;
        const p = (async () => {
            let rounds = 0;
            while (this.inFlight.size > 0 || this.pending.length > 0) {
                if (++rounds > MAX_DRAIN_ROUNDS) {
                    // A non-terminating callback cycle (each completion issues a fresh
                    // async op). Sever it: drop what's queued and abandon the
                    // outstanding ops by bumping the epoch, so their completions are
                    // recognized as stale and never dispatched — otherwise the next
                    // event's drain would just re-enter the same cycle. Warn, and flag
                    // it for the deterministic driver to fail hard on.
                    this.log(`[${this.label}] async drain exceeded ${MAX_DRAIN_ROUNDS} rounds — a db/fetch ` +
                        `callback is re-issuing async ops without terminating; dropping the queue.`, undefined);
                    this.epoch++;
                    this.pending.length = 0;
                    this.inFlight.clear();
                    this.drainLimitHit = true;
                    break;
                }
                if (this.inFlight.size > 0) {
                    await Promise.allSettled([...this.inFlight]);
                }
                this.dispatchPending();
            }
        })();
        this.draining = p;
        // Identity-guarded clear: only this run nulls the guard, and only if
        // it's still the active one. Runs as a microtask, so by the time an
        // `await drain()` continuation resumes the guard is already cleared.
        // Re-arm guard: an op pushed in the window between the loop's last
        // empty-check and this clear would otherwise be stranded (a drain()
        // call in that window joins the already-settling `p`, which has
        // stopped looping). If work remains, kick a fresh drain so the op is
        // dispatched. Still deterministic — microtask-driven, no timers.
        void p.finally(() => {
            if (this.draining !== p)
                return;
            this.draining = null;
            // Don't re-arm after severing a runaway cycle — the queue was
            // deliberately emptied; re-draining would just hit the cap again.
            if (this.drainLimitHit)
                return;
            if (this.inFlight.size > 0 || this.pending.length > 0)
                void this.drain();
        });
        return p;
    }
    // Did the most recent drain() sever a non-terminating callback cycle at
    // MAX_DRAIN_ROUNDS? Deterministic drivers (server settle, the reference
    // host) read this right after awaiting drain() and fail hard, surfacing
    // the runaway instead of silently dropping it.
    hitDrainLimit() {
        return this.drainLimitHit;
    }
    // Dispatch every currently-queued completion back into the script.
    // Synchronous: a dispatched callback may enqueue fresh completions or
    // register new in-flight ops; drain()'s loop picks those up.
    dispatchPending() {
        while (this.pending.length > 0) {
            const op = this.pending.shift();
            try {
                this.dispatchOne(op);
            }
            catch (e) {
                this.log(`[${this.label}] async dispatcher threw on ${op.kind}:`, e);
            }
        }
    }
    dispatchOne(op) {
        const ex = this.getExports();
        switch (op.kind) {
            case "db_get": {
                const fn = ex[CALLBACK_DISPATCHERS.db_get];
                // No top-level cbId guard: fire-and-forget ops with cbId === 0
                // still need their db_error fired (if declared) on infra
                // failures. The dispatcher call alone is skipped.
                if (op.cbId !== 0 && fn !== undefined) {
                    fn(op.cbId, op.success ? 1 : 0, encodeBytes(this.h, op.data), op.ctx);
                }
                if (op.infraError !== undefined)
                    this.fireDbError("get", op.ns, op.key, op.infraError);
                return;
            }
            case "db_list": {
                const fn = ex[CALLBACK_DISPATCHERS.db_list];
                if (op.cbId !== 0 && fn !== undefined) {
                    fn(op.cbId, op.success ? 1 : 0, allocStringArray(this.h, op.keys), op.ctx);
                }
                if (op.infraError !== undefined)
                    this.fireDbError("list", op.ns, op.prefix, op.infraError);
                return;
            }
            case "db_write": {
                // db_set and db_del share a dispatcher (identical callback
                // signatures) — CALLBACK_DISPATCHERS maps both to __cb_db_write.
                const fn = ex[CALLBACK_DISPATCHERS.db_set];
                if (op.cbId !== 0 && fn !== undefined) {
                    fn(op.cbId, op.success ? 1 : 0, op.ctx);
                }
                if (op.infraError !== undefined)
                    this.fireDbError(op.op, op.ns, op.key, op.infraError);
                return;
            }
            case "fetch": {
                const fn = ex[CALLBACK_DISPATCHERS.fetch];
                // The script's callback fires first (success or failure), then
                // — if the op failed and a diagnostic exists — fetch_error.
                // Mirrors db_error ordering.
                if (op.cbId !== 0 && fn !== undefined) {
                    fn(op.cbId, op.success ? 1 : 0, encodeBytes(this.h, op.data), op.ctx);
                }
                if (!op.success && op.errorMsg !== undefined) {
                    this.fireFetchError(op.url, op.errorMsg);
                }
                return;
            }
        }
    }
    fireDbError(op, ns, key, msg) {
        this.onDbError?.(op, ns, key, msg);
        const ex = this.getExports();
        const handlers = [];
        if (this.role !== "server") {
            const fn = ex.event_client_db_error;
            if (fn !== undefined)
                handlers.push({ name: "event_client_db_error", fn });
        }
        if (this.role !== "client") {
            const fn = ex.event_server_db_error;
            if (fn !== undefined)
                handlers.push({ name: "event_server_db_error", fn });
        }
        if (handlers.length === 0)
            return;
        const opPtr = allocString(this.h, op);
        const nsPtr = allocString(this.h, ns);
        const keyPtr = allocString(this.h, key);
        const msgPtr = allocString(this.h, msg);
        for (const handler of handlers) {
            try {
                handler.fn(opPtr, nsPtr, keyPtr, msgPtr);
            }
            catch (e) {
                this.log(`[${this.label}] ${handler.name} threw:`, e);
            }
        }
    }
    fireFetchError(url, msg) {
        this.onFetchError?.(url, msg);
        // fetch_error is client-only per docs/fetch.md — a server-role host
        // records the diagnostic (observer above) but fires no event.
        if (this.role === "server")
            return;
        const ex = this.getExports();
        const fn = ex.event_client_fetch_error;
        if (fn === undefined)
            return;
        const urlPtr = allocString(this.h, url);
        const msgPtr = allocString(this.h, msg);
        try {
            fn(urlPtr, msgPtr);
        }
        catch (e) {
            this.log(`[${this.label}] event_client_fetch_error threw:`, e);
        }
    }
}
//# sourceMappingURL=async-ops.js.map